import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { PLATFORMS, LAUNCHERS, money } from "../lib/api";

export const AccountCard = ({ p, i, onBuy }) => {
  const sold = (p?.stock ?? 0) <= 0;
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  };
  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: (i % 3) * 0.08 }}
      onMouseMove={onMove}
      data-testid={`account-card-${p.id}`}
      className="group relative flex flex-col border border-white/10 bg-[#0f0f16] transition-colors duration-500 hover:border-[#a259ff]/50"
    >
      <div className="pointer-events-none absolute inset-0 z-10 opacity-0 transition-opacity duration-500 group-hover:opacity-100" style={{ background: "radial-gradient(380px circle at var(--mx) var(--my), rgba(162,89,255,0.12), transparent 60%)" }} />
      <div className="relative aspect-[16/10] overflow-hidden">
        <img src={p.image} alt={p.name} className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f16] via-transparent to-transparent" />
        <div className="absolute left-4 top-4 flex gap-1.5">
          <span className="bg-black/70 px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-widest text-violet-200 backdrop-blur">
            {PLATFORMS[p.platform]?.long ?? p.platform}
          </span>
          {LAUNCHERS[p.launcher] && (
            <span className="bg-[#ff3dc8] px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-widest text-white" data-testid="account-card-launcher">
              {LAUNCHERS[p.launcher]}
            </span>
          )}
        </div>
        {p.featured && <span className="absolute right-4 top-4 bg-[#e9d5ff] px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-widest text-black">Hot</span>}
        <div className="absolute bottom-3 left-5 flex items-end gap-6">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-widest text-slate-300">Bank</p>
            <p className="font-mono text-3xl font-extrabold text-[#e9d5ff]" data-testid="account-card-money">{p.money || "—"}</p>
          </div>
          <div>
            <p className="font-mono text-[11px] uppercase tracking-widest text-slate-300">Rank</p>
            <p className="font-mono text-3xl font-extrabold text-white">{p.rank}</p>
          </div>
        </div>
      </div>
      <div className="relative flex flex-1 flex-col p-6">
        <h3 className="font-display text-4xl uppercase leading-none text-white">{p.name}</h3>
        {p.tagline && <p className="mt-2 text-sm text-slate-400">{p.tagline}</p>}
        <ul className="mt-5 flex-1 space-y-2">
          {(p.unlocks ?? []).map((u) => (
            <li key={u} className="flex items-center gap-2.5 text-sm text-slate-200">
              <Check className="h-4 w-4 shrink-0 text-[#a259ff]" /> {u}
            </li>
          ))}
        </ul>
        <div className="mt-6 flex items-center justify-between border-t border-white/10 pt-5">
          <div>
            <p className="font-mono text-2xl font-bold text-white" data-testid="account-card-price">{money(p.price)}</p>
            <p className="font-mono text-[11px] uppercase tracking-widest text-slate-400">{sold ? "Sold out" : `${p.stock} in stock`}</p>
          </div>
          <button
            disabled={sold}
            onClick={() => onBuy(p)}
            data-testid="account-card-buy-button"
            className="relative z-20 flex items-center gap-2 rounded-full bg-[#a259ff] px-5 py-3 font-mono text-xs font-bold uppercase tracking-widest text-black transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_30px_rgba(162,89,255,0.55)] disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-300"
          >
            {sold ? "Sold out" : "Buy now"} {!sold && <ArrowUpRight className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </motion.article>
  );
};
